import React from 'react';
import styled, { keyframes } from 'styled-components';
import WaveDivider from './WaveDivider';

// Animations
const gradientShift = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px) scale(1); }
  33% { transform: translateY(-20px) scale(1.02); }
  66% { transform: translateY(10px) scale(0.98); }
`;

const patternMove = keyframes`
  0% { transform: translateX(0px) translateY(0px); }
  100% { transform: translateX(60px) translateY(60px); }
`;

// Styled Components
const HeroSection = styled.section`
  position: relative;
  background-image: url('./assets/images/bg_01.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  z-index: 1;
  margin-bottom: var(--wave-divider-margin);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, 
      rgba(37, 99, 235, 0.6) 0%, 
      rgba(102, 126, 234, 0.7) 25%, 
      rgba(118, 75, 162, 0.6) 50%, 
      rgba(37, 99, 235, 0.65) 75%, 
      rgba(102, 126, 234, 0.55) 100%),
      linear-gradient(45deg,
      rgba(0, 0, 0, 0.1) 0%,
      rgba(0, 0, 0, 0.2) 100%);
    background-size: 400% 400%;
    animation: ${gradientShift} 20s ease infinite;
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      radial-gradient(circle at 20% 20%, rgba(37, 99, 235, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(118, 75, 162, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 40% 60%, rgba(102, 126, 234, 0.03) 0%, transparent 50%);
    animation: ${float} 25s ease-in-out infinite;
    z-index: 2;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 3;
  text-align: center;
  padding: 8rem 2rem 10rem;
  max-width: 800px;
  margin: 0 auto;
  color: white;
`;

const ProfileImageContainer = styled.div`
  margin-bottom: 2rem;
`;

const ProfileImage = styled.img`
  width: 200px;
  height: 200px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.4);
  }
`;

const HeroTitle = styled.h1`
  font-family: var(--font-heading);
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-family: var(--font-ui);
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 2rem;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  margin-bottom: 3rem;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
  }
`;

const CTAButton = styled.a`
  display: inline-block;
  padding: 1rem 2rem;
  background: linear-gradient(45deg, #667eea 0%, #764ba2 100%);
  color: white;
  text-decoration: none;
  border-radius: 50px;
  font-family: var(--font-ui);
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
    background: linear-gradient(45deg, #764ba2 0%, #667eea 100%);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const Hero = () => {
  return (
    <HeroSection id="home">
      <HeroContent>
        <ProfileImageContainer>
          <ProfileImage 
            src="/images/profile/profile-headshot.jpeg" 
            alt="Nicolette Rudmann - Full-Stack Developerin" 
          />
        </ProfileImageContainer>
        <HeroTitle>Nicolette Rudmann</HeroTitle>
        <Subtitle>Full-Stack Developerin</Subtitle>
        <HeroDescription>
          Software war nie nur Technik für mich – sondern ein Werkzeug, um Probleme elegant zu lösen. 
          Als Fachinformatikerin mit Auszeichnung entwickle ich seit 2022 Web‑ und Microservice‑Lösungen.
        </HeroDescription>
        <CTAButton href="#contact">
          Kontakt aufnehmen
        </CTAButton>
      </HeroContent>
      <WaveDivider 
        color="white" 
        variant="smooth" 
        animated={false}
        height={120}
      />
    </HeroSection>
  );
};

export default Hero; 